import { inject, observer } from 'mobx-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import history from 'helpers/routerHistory';
import * as routes from 'routes';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import BuildIcon from '@material-ui/icons/Build';
import SwapHorizOutlinedIcon from '@material-ui/icons/SwapHorizOutlined';
import PowerSettingsIcon from '@material-ui/icons/PowerSettingsNew';
import Avatar from '@material-ui/core/Avatar';
import EmojiEventsIcon from '@material-ui/icons/EmojiEventsOutlined';
import Divider from '@material-ui/core/Divider';
import ListAltIcon from '@material-ui/icons/ListAlt';
import Publish from '@material-ui/icons/Publish';

import compose from 'helpers/compose';

import { RootStore } from 'stores/rootStore';
import UserLogo from 'components/UserLogo/UserLogo';
import { useStyles } from './styles'; //useListItemTextStyles
import SideBar from 'pages/Accounts/Detail/Sidebar/Sidebar';
import { Permission } from 'helpers/permissions';
import { Client } from 'enums';

interface Props {
  rootStore: RootStore;
  onMenuAction: (menuAction: MenuAction) => void;
  admin?: boolean;
}

export enum MenuAction {
  LOGOUT = 'logout',
  PASSWORD_CHANGE = 'user/password-change',
}

const UserMenu: React.FC<Props> = ({ rootStore, onMenuAction, admin }) => {
  const { t } = useTranslation();
  const classes = useStyles({ admin });
  const { userStore } = rootStore.stores;

  const [mainAnchorEl, setMainAnchorEl] = useState<null | HTMLElement>(null);
  const [accountsOpen, setAccountsOpen] = useState(false);
  const [mainOpen, setMainOpen] = useState(false);
  const [logoOpen, setLogoOpen] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>, menu: 'main' | 'accounts') => {
    setMainAnchorEl(event.currentTarget);
    menu === 'main' ? setMainOpen(true) : setAccountsOpen(true);
  };

  const handleClose = (menu: 'main' | 'accounts') => {
    menu === 'main' ? setMainOpen(false) : setAccountsOpen(false);
    setMainAnchorEl(null);
  };

  if (!userStore?.loggedUser) {
    return null;
  }

  const user = userStore.loggedUser;
  const initials = user.firstname && user.lastname ? `${user.firstname[0]}${user.lastname[0]}` : '';
  const { detail } = rootStore.stores.uiStore;
  const userMode = userStore.checkPermissions(Permission.APP_ENDUSER);
  const signOnly = userStore.loggedUser?.client === Client.CLIENT_PORTAL_SIGN;

  return (
    <>
      {userMode && userStore.clientData?.accounts && userStore.clientData?.accounts.length === 1 && (
        <div className={classes.accountTitle}>
          <div>{userStore.activeAccountName()}</div>
        </div>
      )}

      {userMode && userStore.clientData?.accounts && userStore.clientData?.accounts.length > 1 && (
        <>
          <List className={classes.account}>
            <ListItem button onClick={(e) => handleClick(e, 'accounts')}>
              <ListItemText primary={userStore.activeAccountName()} />
              <ListItemIcon className={classes.arrowIcon}>
                <ArrowDropDownIcon />
              </ListItemIcon>
            </ListItem>
          </List>
          <Menu
            anchorEl={mainAnchorEl}
            open={accountsOpen}
            id="accounts-menu"
            keepMounted
            onClose={() => handleClose('accounts')}
            anchorOrigin={{ horizontal: 0, vertical: 'bottom' }}
            getContentAnchorEl={null}
            className={classes.accountsList}
            transitionDuration={0}
          >
            {userStore.clientData.accounts.map((account) => (
              <MenuItem
                key={account.accountId}
                onClick={
                  userStore.activeAccount?.accountId === account.accountId
                    ? undefined
                    : () => {
                        userStore.setActiveAccount(account);
                        handleClose('accounts');
                      }
                }
                className={userStore.activeAccount?.accountId === account.accountId ? classes.active : ''}
              >
                <ListItemIcon>
                  <SwapHorizOutlinedIcon />
                </ListItemIcon>
                <ListItemText>
                  {account.title}
                  {account.activeStatus && <> ({t(`options_activeStatus_${account.activeStatus}`)})</>}
                </ListItemText>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      <List
        className={`${classes.avatar} ${userMode ? 'userMode' : ''} ${signOnly ? 'signOnly' : ''} ${
          detail && userStore.checkPermissions(Permission.APP_ADVISOR) ? 'detail' : ''
        }`}
      >
        <ListItem button onClick={(e) => handleClick(e, 'main')}>
          <Avatar className={classes.avatarIcon}>{initials}</Avatar>
          <ListItemIcon className={classes.arrowIcon}>
            <ArrowDropDownIcon />
          </ListItemIcon>
        </ListItem>
      </List>

      <Menu
        anchorEl={mainAnchorEl}
        open={mainOpen}
        id="user-menu"
        keepMounted
        onClose={() => handleClose('main')}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        getContentAnchorEl={null}
        className={classes.userMenu}
        transitionDuration={0}
      >
        {userStore.loggedUser?.client !== Client.CLIENT_PORTAL_SIGN && (
          <MenuItem onClick={() => userStore.accountManagement()}>
            <ListItemIcon>
              <BuildIcon />
            </ListItemIcon>
            <ListItemText>{t('navMenu_setupAccount')}</ListItemText>
          </MenuItem>
        )}
        {userStore.checkPermissions(Permission.ACCESS_BASIC) && (
          <MenuItem onClick={() => history.push(routes.ADVISOR_RANKINGS)}>
            <ListItemIcon>
              <EmojiEventsIcon />
            </ListItemIcon>
            <ListItemText>{t('advisor_menu_RANKINGS')}</ListItemText>
          </MenuItem>
        )}
        {userStore.checkPermissions(Permission.ACCESS_BASIC) && (
          <MenuItem onClick={() => history.push(routes.ACCOUNTS_PAGE)}>
            <ListItemIcon>
              <ListAltIcon />
            </ListItemIcon>
            <ListItemText>{t('backOffice_accounts_title')}</ListItemText>
          </MenuItem>
        )}
        {userStore.checkPermissions(Permission.ACCESS_BASIC) && userStore.loggedUser.logo !== null && (
          <MenuItem onClick={() => setLogoOpen(true)}>
            <ListItemIcon>
              <Publish />
            </ListItemIcon>
            <ListItemText>{t('navMenu_logo')}</ListItemText>
          </MenuItem>
        )}

        <Divider />

        <MenuItem onClick={() => onMenuAction(MenuAction.LOGOUT)}>
          <ListItemIcon>
            <PowerSettingsIcon />
          </ListItemIcon>
          <ListItemText>{t('general_logout')}</ListItemText>
        </MenuItem>
      </Menu>

      {userMode && <SideBar rootStore={rootStore} header />}

      {logoOpen && <UserLogo opened={logoOpen} handleClose={() => setLogoOpen(false)} />}
    </>
  );
};

export default compose(inject('userStore', 'uiStore', 'rootStore'), observer)(UserMenu);
